import { ArrowRightIcon, PencilIcon } from '@heroicons/react/20/solid';

import { Button, EButtonType } from '@/components/Button';
import { ContactBox } from '@/components/ContactBox';
import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { OutcomeCard } from '@/components/OutcomeCard';
import { ProgressBar } from '@/components/ProgressBar';
import { RemoteImage } from '@/components/RemoteImage';
import { selectIsVehicleKnown } from '@/context/borrower';
import { EDisplays, setDisplay } from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';

export const testId = 'screen-servicingFailure';

export function ServicingFailure(): JSX.Element {
    const baseId = 'screen-servicingFailure';
    // ***** Redux State *****
    const dispatch = useAppDispatch();

    // ***** Event Handlers *****
    function handleReduceLoanAmount(): void {
        dispatch(setDisplay(EDisplays.LOAN));
    }

    function handleEditIncome(): void {
        dispatch(setDisplay(EDisplays.INCOME));
    }

    const state = store.getState();
    const { outcomes, contact, failureCard } = useCopy();
    const outcome = outcomes.servicingFailure;
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);
    const isNovatedLease = state.displayReducer.partner.isNovatedLease;

    if (failureCard) {
        return (
            <div data-testid={testId}>
                <DisplayComponent baseId={baseId}>
                    <Header />
                    <ProgressBar />
                    <OutcomeCard
                        icon={<PencilIcon className='h-5 w-5' />}
                        tint='bg-optionTrack text-panel'
                        title={outcome.title}
                        lines={outcome.body}
                    >
                        {/* Reviewing the answers leads; reducing the amount is
                            the fallback, so it reads as the quieter option. */}
                        <Button
                            id={`${baseId}-button-editAnswers`}
                            onClick={handleEditIncome}
                            type={EButtonType.CARD_PRIMARY}
                        >
                            <span className='_flex items-center gap-2 text-[15px]'>
                                {contact.editAnswers}
                                <ArrowRightIcon className='h-4 w-4' />
                            </span>
                        </Button>
                        {!isNovatedLease && isVehicleKnown && (
                            <Button
                                id={`${baseId}-button-reduceLoan`}
                                onClick={handleReduceLoanAmount}
                                type={EButtonType.CARD_SECONDARY_OUTLINE}
                            >
                                <span className='text-[15px]'>
                                    {contact.reduceLoan}
                                </span>
                            </Button>
                        )}
                    </OutcomeCard>
                    <ContactBox />
                </DisplayComponent>
            </div>
        );
    }

    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />
                <div className='flex-center pb-4 relative  pt-4'>
                    <div className='bg-primary h-40 w-40 rounded-full' />
                    <RemoteImage fileName='coins.svg' className='absolute' />
                </div>
                <div className='text-center _flex flex-col space-y-2  pt-4'>
                    <h3 className='uppercase text-xl text-primary font-bold whitespace-pre-line'>
                        {outcome.title}
                    </h3>
                    {outcome.body.map((line, i) => (
                        <p
                            key={line}
                            className={`whitespace-pre-line ${
                                i === 0 ? 'text-secondary ' : 'text-sm'
                            }`}
                        >
                            {line}
                        </p>
                    ))}
                </div>
                <div className='_flex space-x-2 mx-4 pt-4'>
                    {isNovatedLease ||
                        (isVehicleKnown && (
                            <Button
                                id={`${baseId}-button-reduceLoan`}
                                onClick={handleReduceLoanAmount}
                            >
                                {contact.reduceLoan}
                            </Button>
                        ))}
                    <Button
                        id={`${baseId}-button-editAnswers`}
                        onClick={handleEditIncome}
                    >
                        {contact.editAnswers}
                    </Button>
                </div>
                <div className='flex-center w-full'>
                    <ContactBox />
                </div>
            </DisplayComponent>
        </div>
    );
}
